import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Save } from 'lucide-react';
import { useCreateUser } from '../../hooks/useUsers';
import PageHeader from '../../components/common/PageHeader';

interface UserFormValues {
  email: string;
  name: string;
  firstName: string;
  lastName: string;
}

export default function UserCreatePage() {
  const navigate = useNavigate();
  const createUser = useCreateUser();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UserFormValues>();

  const onSubmit = (values: UserFormValues) => {
    createUser.mutate(values, {
      onSuccess: (user) => {
        navigate(`/users/${encodeURIComponent(user.urn)}`);
      },
    });
  };

  return (
    <div>
      <PageHeader
        title="Create User"
        actions={
          <button onClick={() => navigate('/users')} className="btn-secondary gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        }
      />

      <form onSubmit={handleSubmit(onSubmit)} className="max-w-lg space-y-4 lg:space-y-6">
        <div className="card p-4 lg:p-6">
          <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 lg:mb-4">User Information</h2>
          <div className="space-y-4">
            <div>
              <label className="label">Email *</label>
              <input
                type="email"
                className="input"
                placeholder="user@example.com"
                {...register('email', { required: 'Email is required' })}
              />
              {errors.email && <p className="text-sm text-red-500 mt-1">{errors.email.message}</p>}
            </div>
            <div>
              <label className="label">Name *</label>
              <input
                type="text"
                className="input"
                placeholder="Full name"
                {...register('name', { required: 'Name is required' })}
              />
              {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name.message}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">First Name</label>
                <input type="text" className="input" {...register('firstName')} />
              </div>
              <div>
                <label className="label">Last Name</label>
                <input type="text" className="input" {...register('lastName')} />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => navigate('/users')} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={createUser.isPending} className="btn-primary gap-1">
            <Save className="h-4 w-4" />
            {createUser.isPending ? 'Creating...' : 'Create User'}
          </button>
        </div>

        {createUser.isError && (
          <p className="text-sm text-red-500">
            Failed: {(createUser.error as Error).message}
          </p>
        )}
      </form>
    </div>
  );
}
